/**
 * Kecilkan gambar di peramban sebelum diunggah.
 *
 * TIGA ALASAN, dan yang ketiga paling mahal:
 *
 *   1. nginx menolak badan di atas `client_max_body_size` dengan halaman HTML 413 yang
 *      tidak menyebut batas apa pun.
 *   2. Bandwidth dan penyimpanan.
 *   3. Referensi ikut dikirim ke model sebagai data URI pada SETIAP pembuatan gambar.
 *      Berkas 6 MB jadi ~8 MB base64 di tiap permintaan, ditagih berulang — sementara
 *      model mengecilkannya sendiri di dalam dan tidak memakai resolusi setinggi itu.
 *
 * TIDAK MENYENTUH YANG SUDAH KECIL. Berkas di bawah ambang dan di bawah dimensi maksimum
 * dikembalikan APA ADANYA, tanpa dibuka dan tanpa dikodekan ulang. Mengodekan ulang
 * gambar yang sudah pas cuma menambah kerugian generasi tanpa menghemat apa pun.
 *
 * PNG BERTRANSPARANSI TETAP PNG. Mengubahnya jadi JPEG mengisi bagian transparan dengan
 * hitam — dan referensi gaya brand sering berupa logo di atas latar transparan.
 */

export const MAKS_DIMENSI = 1536
export const AMBANG_BYTE = 1024 * 1024        // di bawah ini, tidak disentuh
export const MUTU_JPEG = 0.88

/**
 * Dimensi hasil pengecilan. Dipisah supaya bisa diuji tanpa DOM — di sinilah kesalahan
 * tidak akan terlihat: gambar yang melar atau gepeng lolos build, lolos unggah, dan baru
 * terlihat sebagai referensi yang bentuknya salah.
 *
 * TIDAK PERNAH MEMPERBESAR: `Math.min(1, ...)` menjaga gambar yang sudah kecil tetap
 * seukurannya. Memperbesar cuma menambah byte tanpa menambah satu detail pun.
 */
export function hitungUkuran(lebar, tinggi, maks) {
  const skala = Math.min(1, maks / Math.max(lebar, tinggi))
  return {
    lebar: Math.max(1, Math.round(lebar * skala)),
    tinggi: Math.max(1, Math.round(tinggi * skala)),
  }
}

function muat(berkas) {
  return new Promise((selesai, gagal) => {
    const url = URL.createObjectURL(berkas)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); selesai(img) }
    img.onerror = () => { URL.revokeObjectURL(url); gagal(new Error('bukan gambar')) }
    img.src = url
  })
}

/** Apakah gambar punya piksel tembus pandang? Diperiksa, bukan ditebak dari ekstensi. */
function adaTransparansi(kanvas, ctx) {
  const { width: w, height: h } = kanvas
  // Dicuplik, bukan dipindai seluruhnya: gambar 1536² berarti 2,4 juta piksel, dan
  // memeriksa semuanya membekukan tab beberapa ratus milidetik tanpa alasan.
  const langkah = Math.max(1, Math.floor(Math.min(w, h) / 64))
  for (let y = 0; y < h; y += langkah) {
    const baris = ctx.getImageData(0, y, w, 1).data
    for (let x = 3; x < baris.length; x += 4 * langkah) {
      if (baris[x] < 255) return true
    }
  }
  return false
}

/**
 * Mengembalikan `{ berkas, diubah, sebelum, sesudah }`.
 *
 * `diubah` false berarti berkas aslinya dikembalikan utuh. Pemanggil WAJIB menampilkan
 * perubahannya kalau true — mengganti berkas orang diam-diam adalah kejutan, sekecil
 * apa pun manfaatnya.
 */
export async function kecilkan(berkas, { maks = MAKS_DIMENSI, ambang = AMBANG_BYTE } = {}) {
  const sebelum = { bytes: berkas.size, lebar: null, tinggi: null }

  if (berkas.size <= ambang) {
    return { berkas, diubah: false, sebelum, sesudah: sebelum }
  }

  let img
  try {
    img = await muat(berkas)
  } catch {
    // Bukan gambar yang bisa dibuka peramban — dikembalikan apa adanya supaya server
    // yang menolaknya dengan pesannya sendiri. Menolak di sini akan menyembunyikan
    // sebabnya di balik kalimat yang lebih samar.
    return { berkas, diubah: false, sebelum, sesudah: sebelum }
  }

  sebelum.lebar = img.naturalWidth
  sebelum.tinggi = img.naturalHeight

  const { lebar: w, tinggi: h } = hitungUkuran(img.naturalWidth, img.naturalHeight, maks)

  const kanvas = document.createElement('canvas')
  kanvas.width = w
  kanvas.height = h
  const ctx = kanvas.getContext('2d')
  ctx.drawImage(img, 0, 0, w, h)

  const transparan = adaTransparansi(kanvas, ctx)
  const tipe = transparan ? 'image/png' : 'image/jpeg'
  const mutu = transparan ? undefined : MUTU_JPEG

  const blob = await new Promise((r) => kanvas.toBlob(r, tipe, mutu))
  if (!blob) return { berkas, diubah: false, sebelum, sesudah: sebelum }

  // KALAU HASILNYA TIDAK LEBIH KECIL, aslinya yang dipakai. Terjadi pada PNG kecil
  // beresolusi tinggi yang sudah terkompresi baik — mengubahnya cuma memperbesar.
  if (blob.size >= berkas.size) {
    return { berkas, diubah: false, sebelum, sesudah: sebelum }
  }

  const ext = transparan ? 'png' : 'jpg'
  const nama = berkas.name.replace(/\.[^.]+$/, '') + '.' + ext
  return {
    berkas: new File([blob], nama, { type: tipe }),
    diubah: true,
    sebelum,
    sesudah: { bytes: blob.size, lebar: w, tinggi: h },
  }
}

/** Batas carousel Instagram — sama dengan CHECK di migrasi 0031. */
export const MAKS_SLIDE = 10

/**
 * Berapa GAMBAR yang dibuat sehari oleh sebuah jadwal terbit.
 *
 * BUKAN jumlah slot, dan bedanya menentukan tagihan. Sejak slot bisa collase, satu slot
 * bisa berarti tiga gambar — "4 konten per hari" tidak berubah saat satu slot dinaikkan
 * jadi 3 slide, padahal belanjanya naik dua kali lipat.
 *
 * SLOT VIDEO TIDAK IKUT DIHITUNG. Tarifnya berbeda, sebagian model video belum punya
 * tarif sama sekali, dan menjumlahkannya dengan gambar menghasilkan satu angka yang tidak
 * berarti apa-apa untuk keduanya.
 *
 * Nilai `slide` yang aneh — nol, negatif, kosong, teks — dibaca sebagai 1. Slot warisan
 * yang tersimpan sebelum kolom ini ada tidak punya medannya sama sekali, dan slot itu
 * memang satu gambar.
 */
export function hitungGambarPerHari(slot) {
  return (slot ?? [])
    .filter((x) => x?.media_type !== 'video')
    .reduce((n, x) => {
      const s = Number(x?.slide)
      return n + (Number.isFinite(s) ? Math.max(1, Math.min(MAKS_SLIDE, Math.floor(s))) : 1)
    }, 0)
}
